/**
 * The one place filter values are WRITTEN.
 *
 * Setting, resetting one, and resetting all had three separate
 * implementations — the portal's ``setFilter`` / ``clearFilters`` and
 * the builder Preview's inline ``onFilterChange`` / ``onClear`` — and
 * they disagreed on the two cases that bite:
 *
 * - Clearing a value: a date-window filter keeps an explicit ``""``
 *   sentinel (so the engine skips it instead of re-applying the report
 *   default), while a plain select drops its key entirely. Both apps
 *   had this logic copy-pasted; only one was ever pinned.
 * - "Clear all": the portal kept locked + the required filter's CURRENT
 *   value; the builder blanked everything to ``{}``. Neither reset to
 *   the report's declared defaults, so "clear" was really "clear to
 *   whatever the backend falls back to".
 *
 * These pure helpers return a NEW active-filter dict (never mutate) and
 * define the semantics once. "Reset" means **go back to the report
 * default** (Pablo, 2026-07-20): a filter with a default returns to it,
 * a required filter without one re-seeds from its options (never left
 * empty — that is the state that used to strand the dashboard with no
 * way back short of a reload), and a plain optional filter goes to
 * "All". Because the reset dict equals the report defaults, the
 * first-page cache key is unchanged and stays warm.
 */

import { resolveFilterDefault, resolveSeedValue } from "./filter-state";
import { isDateWindowType, type DashboardFilter } from "./types";

type ActiveFilters = Record<string, string>;

/**
 * Set (or clear) one filter's value.
 *
 * ``value === ""`` means the user cleared the control:
 * - date-window and ``range`` types keep the ``""`` sentinel so the
 *   engine skips the filter (and does NOT re-apply the default on the
 *   next render), see ``keepsClearedSentinel``;
 * - every other type drops the key entirely (``= "All"``).
 */
export function setFilterValue(
  active: Readonly<ActiveFilters>,
  filt: Pick<DashboardFilter, "key" | "type">,
  value: string,
  filters: ReadonlyArray<DashboardFilter> = [],
): ActiveFilters {
  const next: ActiveFilters = { ...active };
  writeValue(next, filt, value);
  return clearDependentFilters(active, next, [filt.key], filters);
}

/**
 * Set (or clear) SEVERAL filters in one write: a KPI action jump (DSA
 * 1.5) that names ``{filter: value}`` pairs. Portal and builder Preview
 * both go through here, so the two apply an action identically.
 *
 * Each value follows ``setFilterValue`` (``""`` clears, with the
 * date-window sentinel). Then the descendants of every key the action
 * CHANGED are invalidated, except the keys the action wrote itself: an
 * action that sets a child and its parent keeps both, in whatever order
 * the mapping lists them. (Writing them one by one let the parent clear
 * the child the same action had just asked for.) Keys that are not a
 * filter of the report are ignored, as the portal always did; publish
 * already refuses an action naming one.
 */
export function applyFilterValues(
  active: Readonly<ActiveFilters>,
  writes: Readonly<Record<string, string>>,
  filters: ReadonlyArray<DashboardFilter>,
): ActiveFilters {
  const next: ActiveFilters = { ...active };
  const written = new Set<string>();
  for (const [key, value] of Object.entries(writes)) {
    const filt = filters.find((f) => f.key === key);
    if (!filt) continue;
    writeValue(next, filt, value);
    written.add(key);
  }
  return clearDependentFilters(active, next, written, filters, written);
}

/**
 * Types whose clear must travel as ``""`` instead of dropping the key.
 * Both carry a JSON ``default_value`` the engine re-applies to an ABSENT
 * key: a date window, and a numeric ``range`` threshold (the Explorer's
 * "3P share >= 30 %"). Dropping the key would make the cleared control
 * snap back to its default on the next request, so the user could never
 * see the unbounded list.
 */
export function keepsClearedSentinel(type: string | null | undefined): boolean {
  return isDateWindowType(type) || type === "range";
}

function writeValue(
  next: ActiveFilters,
  filt: Pick<DashboardFilter, "key" | "type">,
  value: string,
): void {
  if (value === "") {
    if (keepsClearedSentinel(filt.type)) next[filt.key] = "";
    else delete next[filt.key];
  } else {
    next[filt.key] = value;
  }
}

/** Invalidate descendants atomically; empty sentinels suppress old defaults.
 * Locked filters remain enforced, and so do the ``keep`` keys (the ones a
 * batch write set on purpose). Reports without dependencies are unchanged.
 */
function clearDependentFilters(
  previous: Readonly<ActiveFilters>,
  next: ActiveFilters,
  keys: Iterable<string>,
  filters: ReadonlyArray<DashboardFilter>,
  keep: ReadonlySet<string> = new Set(),
): ActiveFilters {
  const changed = new Set(
    [...keys].filter((key) => (previous[key] ?? "") !== (next[key] ?? "")),
  );
  if (changed.size === 0) return next;
  for (const filter of filters) {
    if (
      filter.locked ||
      keep.has(filter.key) ||
      !filter.depends_on?.some((parent) => changed.has(parent))
    ) {
      continue;
    }
    next[filter.key] = "";
    changed.add(filter.key);
  }
  return next;
}

/**
 * Drop multi-select values that no longer belong to the option universe
 * returned by the latest cascade.
 *
 * The target filter is self-excluded by the engine, so its response is the
 * complete set allowed by its siblings. When Storefront changes, this turns
 * a stale Sellers selection into either the still-valid intersection or All,
 * instead of leaving the dashboard constrained by an invisible old seller.
 * Missing option keys are ignored for compatibility with older engines;
 * an explicit empty list is authoritative.
 */
export function reconcileMultiSelectOptions(
  active: Readonly<ActiveFilters>,
  filters: ReadonlyArray<DashboardFilter>,
  optionsByKey: Readonly<Record<string, ReadonlyArray<string>>>,
): ActiveFilters {
  let next: ActiveFilters = active as ActiveFilters;

  for (const filt of filters) {
    if (filt.type !== "multi_select") continue;
    const raw = active[filt.key];
    if (!raw) continue;

    const authored = filt.options?.length ? filt.options : undefined;
    const hasDynamic = Object.prototype.hasOwnProperty.call(optionsByKey, filt.key);
    const options = authored ?? (hasDynamic ? optionsByKey[filt.key] : undefined);
    if (options === undefined) continue;

    let decoded: unknown;
    try {
      decoded = JSON.parse(raw);
    } catch {
      continue;
    }
    if (!Array.isArray(decoded)) continue;

    const available = new Set(options);
    const selected = decoded.map(String);
    const kept = selected.filter(
      (value, index) => available.has(value) && selected.indexOf(value) === index,
    );
    if (
      kept.length === selected.length &&
      kept.every((value, index) => value === selected[index])
    ) {
      continue;
    }

    if (next === active) next = { ...active };
    if (kept.length > 0) {
      next[filt.key] = JSON.stringify(kept);
    } else if (filt.required) {
      const seed = resolveSeedValue(filt, options);
      if (seed === null) delete next[filt.key];
      else next[filt.key] = seed;
    } else {
      delete next[filt.key];
    }
  }

  return next;
}

/**
 * Reset one filter to the report default.
 *
 * - has a default (or ``locked``) → restore it;
 * - required without a default → re-seed from ``options`` (the loaded
 *   cascade options for that filter); if nothing can be seeded, drop
 *   the key rather than write junk;
 * - optional without a default → drop the key ("All").
 *
 * Never leaves a required filter empty — that is the irrecoverable
 * state the old per-filter clear could produce.
 */
export function resetFilterValue(
  active: Readonly<ActiveFilters>,
  filt: DashboardFilter,
  options: ReadonlyArray<string>,
  today: Date,
  filters: ReadonlyArray<DashboardFilter> = [],
): ActiveFilters {
  const next: ActiveFilters = { ...active };
  const dflt = resolveFilterDefault(filt, today);
  if (dflt !== null) {
    next[filt.key] = dflt;
    return clearDependentFilters(active, next, [filt.key], filters);
  }
  if (filt.required) {
    const seed = resolveSeedValue(filt, options);
    if (seed !== null) next[filt.key] = seed;
    else delete next[filt.key];
    return clearDependentFilters(active, next, [filt.key], filters);
  }
  delete next[filt.key];
  return clearDependentFilters(active, next, [filt.key], filters);
}

/**
 * Reset EVERY filter to the report default — what "Clear all" does.
 *
 * The result equals the report's default-filter dict (locked +
 * ``default_value``) plus a seed for any required filter that declares
 * no default. That is exactly the report's initial state, so the
 * first-page cache treats it as a first-page request and serves warm.
 *
 * ``optionsByKey`` supplies the loaded cascade options used to seed
 * required-without-default filters; omit it (or leave a key out) and
 * such a filter is dropped for the seeding effect to fill in.
 */
export function resetAllFilters(
  filters: ReadonlyArray<DashboardFilter>,
  optionsByKey: Readonly<Record<string, ReadonlyArray<string>>>,
  today: Date,
): ActiveFilters {
  const out: ActiveFilters = {};
  for (const filt of filters) {
    const dflt = resolveFilterDefault(filt, today);
    if (dflt !== null) {
      out[filt.key] = dflt;
      continue;
    }
    if (filt.required && !filt.depends_on?.length) {
      const seed = resolveSeedValue(filt, optionsByKey[filt.key] ?? []);
      if (seed !== null) out[filt.key] = seed;
    }
    // optional without a default → absent = "All"
  }
  return out;
}


// ── Cross-filter write / read (tables, chart legends, KPI actions) ──
//
// A click on a cell / legend key / "Filter" button writes ONE value into
// a report filter. Scalar targets (select / autocomplete / tabs) take the
// value as-is and a second click on the same value clears it. A
// ``multi_select`` target holds a JSON array: the click writes
// ``["<value>"]`` — a one-element selection, never appended to whatever
// was selected before (the click means "look at THIS one") — and a
// second click on the active value clears it. Both hosts (portal page,
// builder preview) write through ``crossFilterWrite``; every reader that
// highlights the active value (DataTable cells and action buttons, chart
// legends) reads through ``crossFilterActiveValue``, so the two halves
// cannot drift.

/** ``null`` = not a JSON array; ``[]``-shaped values come back as the
 *  parsed list (so a two-element selection is "an array, no scalar"). */
function parseSelection(active: string | undefined): string[] | null {
  if (!active || active[0] !== "[") return null;
  try {
    const parsed: unknown = JSON.parse(active);
    return Array.isArray(parsed) ? parsed.map((v) => String(v)) : null;
  } catch {
    return null;
  }
}

function parseSingleSelection(active: string | undefined): string | null {
  const list = parseSelection(active);
  return list && list.length === 1 ? list[0]! : null;
}

/** The value a cross-filter click should write for ``filterType`` given
 *  the filter's current ``active`` value; ``""`` means clear. */
export function crossFilterWrite(
  filterType: string | null | undefined,
  active: string | undefined,
  value: string,
): string {
  if (filterType === "multi_select" || filterType === "chip") {
    return parseSingleSelection(active) === value ? "" : JSON.stringify([value]);
  }
  return active === value ? "" : value;
}

/** The scalar a reader compares a cell / legend key against: the active
 *  value itself, or the element of a one-element JSON selection (what a
 *  cross-filter click writes into a multi_select). ``null`` when nothing
 *  comparable is active. Type-agnostic on purpose: readers sit below the
 *  filter registry and a scalar that happens to look like ``["x"]`` is
 *  not a value any report filter holds. */
export function crossFilterActiveValue(active: string | undefined): string | null {
  if (active == null || active === "") return null;
  const list = parseSelection(active);
  if (list) return list.length === 1 ? list[0]! : null;
  return active;
}
